import axios from 'axios';
import { useEffect, useState, useRef} from 'react';
import { RoomContext, useTranscriptions, useChat, useTracks, RoomAudioRenderer, BarVisualizer, TrackRefContext, VideoTrack, useTrackToggle } from '@livekit/components-react';
import { Room, RoomEvent,Track } from 'livekit-client';
import '@livekit/components-styles';
import { Button, Container } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import "bootstrap-icons/font/bootstrap-icons.css";
import { Rnd } from 'react-rnd'
import { useNavigate } from 'react-router-dom';
import Mapping from '../data/mapping.json'

function AvatarRenderer({isFallback, setHasCamTrack, onFallback, startChat }) {
    const trackRefs = useTracks([Track.Source.Camera]);
    const camTrackRef = trackRefs.find(
        (trackRef) => trackRef.participant.name === 'avatar-agent'
    );

    useEffect(() => {
        if (camTrackRef && !isFallback) {
            setHasCamTrack(true);
            return;
        }
        if (!camTrackRef && !isFallback) {
            const intervalId = setInterval(() => {
                onFallback(); // Tell Avatar to disconnect the room and set fallback UI
            }, 10000);
            return () => clearInterval(intervalId);
        }
    }, [camTrackRef, isFallback, setHasCamTrack, onFallback]);

    if (isFallback){
       startChat();
    }

    // Normal avatar video
    if (camTrackRef) {
        return (
            <div className="video-wrapper position-relative">
                <VideoTrack
                    trackRef={camTrackRef}
                    className='video-el'
                />
                <RoomAudioRenderer />
            </div>
        );
    }

    // Loading placeholder until avatar loads 
    if (!camTrackRef && !isFallback) {
        return (
            <div className={`image-wrapper loading`}>
                    <img class="avatar-img"
                        src={process.env.PUBLIC_URL + "/avatar-meera.webp"}
                        alt="preview"
                        onload="this.parentElement.classList.remove('loading')"
                    />
                    <div class={`image-overlay`}>
                        <img src="/loader2.gif" alt="loading" class="loader-image" />
                    </div>
                </div>
        );
    }

    return null;
}

const MuteButton = () => {

    const { buttonProps, enabled } = useTrackToggle({
        source: Track.Source.Microphone,
        initialState: true
    });

    return (
        <Button variant='light' onClick={buttonProps.onClick} className="control-btn rounded-circle mute-btn">
            <i className={`bi ${enabled ? "bi-mic-fill" : "bi-mic-mute-fill"}`} />
        </Button>
    );
};

const AudioWavesRenderer = () =>{
    const audioTracks = useTracks([Track.Source.Microphone]);
    const audioTrackRef = audioTracks[0];
          return (
                <TrackRefContext.Provider value={audioTrackRef}>
                  <BarVisualizer barCount={8} options={{ minHeight: 8 }} />
                </TrackRefContext.Provider>
          );
      
}

const TranscriptBox = () =>{
    const transcriptions = useTranscriptions();
    const boxRef = useRef(null);

    useEffect(() => {
        if (boxRef.current) {
            boxRef.current.scrollTop = boxRef.current.scrollHeight;
        }
    }, [transcriptions]);

    return (
        <div className="d-flex flex-column gap-2 h-100 overflow-auto p-3 bg-dark" ref={boxRef}>
            {transcriptions.map((transcription, idx) =>{
                const isAgent = transcription.participantInfo.identity.includes("agent");
                return (
                    <div key={idx} className={`d-flex ${isAgent ? "justify-content-start text-dark" : "justify-content-end text-light"}`}>
                        <div className="p-2 rounded fw-bold" style={{ maxWidth: "80%", fontSize: "13px", backgroundColor: isAgent ? "#ffffff":"#7b8983" }}>
                            <strong>{isAgent ? "Agent" : "You"}:</strong> {transcription.text}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}


const ChatBox = () =>{
    const {send} = useChat();
    const transcriptions = useTranscriptions();
    const boxRef = useRef(null);
    const [input, setInput] = useState("");
    const [userMessages, setUserMessages] = useState([]);
    
    const handleSend = async () => {
        if (!input.trim()) return;
        setUserMessages(prev => [...prev, input]);
        await send(input);
        setInput("");
    };

    // Agent responses directly from transcriptions (auto-updates, no copying)
    const agentResponses = transcriptions.filter(t => t.participantInfo?.identity?.includes("agent"));

    useEffect(() => {
        if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
    }, [userMessages, transcriptions]);

    return (
        <div className="d-flex flex-column h-100 bg-dark">
            <div className="flex-grow-1 overflow-auto d-flex flex-column gap-2 p-3" ref={boxRef}>
                {agentResponses[0]?.text ? (
                    <div className="d-flex justify-content-start text-dark">
                        <div className="p-2 rounded fw-bold" style={{ maxWidth: "80%", fontSize: "13px", backgroundColor: "#ffffff" }}>
                            {agentResponses[0].text}
                        </div>
                    </div>
                ):(
                    <div className="d-flex justify-content-start text-light">
                        <div className="p-2 rounded fw-bold">
                            <div className="spinner-border text-light" role="status">
                                <span className="visually-hidden">Thinking...</span>
                            </div>
                        </div>
                    </div>
                )}
                {userMessages && userMessages.map((text, i) => (
                    <div key={i}>
                        <div className="d-flex justify-content-end text-light">
                            <div className="p-2 rounded fw-bold" style={{ maxWidth: "80%", fontSize: "13px", backgroundColor: "#7b8983" }}>
                                {text}
                            </div>
                        </div>
                        {agentResponses[i + 1] && (
                            <div className="d-flex justify-content-start text-dark mt-2">
                                <div className="p-2 rounded fw-bold" style={{ maxWidth: "80%", fontSize: "13px", backgroundColor: "#ffffff" }}>
                                    {agentResponses[i + 1].text}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                
            </div>

            <div className="d-flex align-items-center gap-2 p-2 border-top border-secondary">
                <input
                    className="form-control form-control-sm bg-light text-dark border-0"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type a message..."
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    autoFocus
                    disabled={!agentResponses[0]?.text}
                />
                <button className="btn btn-sm fw-bold" style={{backgroundColor: "#628274ff"}} onClick={handleSend} disabled={!input.trim()}>
                    <i className="bi bi-send-fill text-white fs-6"></i>
                </button>
            </div>
        </div>
    );
}

function Avatar(props){
       axios.defaults.baseURL = process.env.REACT_APP_BASE_URL;
       const [room] = useState(() => new Room({}));
       const [showAvatar, setShowAvatar] = useState(false);
       const [showTranscript, setShowTranscript] = useState(false);
       const [showChatBot, setShowChatBot] = useState(false);
       const [isFallback, setIsFallback] = useState(false);
       const [hasCamTrack, setHasCamTrack] = useState(false);
       const [agentName, setAgentName] = useState(null);
       const [roomName, setRoomName] = useState(null);
       const [loading, setLoading] = useState('');
       const [imageLoaded, setImageLoaded] = useState(true);
       const DEFAULT_WIDTH = 380;
       const DEFAULT_HEIGHT = 400;
       const MARGIN = 16;
       const MOBILE_BREAKPOINT = 480;
       const MOBILE_WIDTH = 350;
       const MOBILE_HEIGHT = 260;

        const [size, setSize] = useState({
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
        });

        const [position, setPosition] = useState({
        x: window.innerWidth - DEFAULT_WIDTH - MARGIN,
        y: window.innerHeight - DEFAULT_HEIGHT - MARGIN,
        });
       
        // const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

       // Make the floating window a bit smaller on narrow screens and keep it anchored
       useEffect(() => {
        const applyResponsive = () => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
       
        const width =
            vw <= MOBILE_BREAKPOINT
            ? Math.min(MOBILE_WIDTH, vw - MARGIN * 2)
            : DEFAULT_WIDTH;

        const height =
            vw <= MOBILE_BREAKPOINT
            ? Math.min(MOBILE_HEIGHT, vh - MARGIN * 2)
            : DEFAULT_HEIGHT;

        const x = Math.max(MARGIN, vw - width - MARGIN);
        const y = Math.max(MARGIN, vh - height - MARGIN);

        setSize({ width, height });
        setPosition({ x, y });
        };
        applyResponsive();
        window.addEventListener('resize', applyResponsive);
        return () => window.removeEventListener('resize', applyResponsive);
       }, []);
       const navigate = useNavigate();    
       const currentPage = window.location.pathname;

       const tabToSectionMap={
         "AI":"AI",
         "Services":"Services",
         "Insights":"Artifacts",
         "Technologies":"TechStack",
         "Leaders":"Leadership"
       }

       room.on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
          
            if (topic==='navigation'){
                const page = new TextDecoder().decode(payload).trim();
                 console.log("Navigating to : ", page)
                
                 if (!page || page.toLowerCase()==="home"){
                     props.setShowWhatWeDo(false)
                     navigate('/')
                 }

                if(page.toLowerCase()==="capabilities"){

                    props.setWhatWeDo('Capabilities')
                    props.setShowWhatWeDo(true)
                    // window.scrollTo(0, 0);
                }
                    
                if(page.toLowerCase()==="industries"){
                     props.setWhatWeDo('Industries')
                     props.setShowWhatWeDo(true)
                     // window.scrollTo(0, 0);
                }

                if(page.startsWith('Subpages-')){
                    props.setShowWhatWeDo(false)
                    const capability_name = page.split('-')[1]
                    const tab = page.split('-')[2] 
                    const section = tab ? tabToSectionMap[tab] : 'top'
                    const capability = Mapping.find((capability)=>capability.Desc===capability_name)
                    const shortDesc=capability.ShortDesc
                    if (shortDesc){
                      navigate(`/capabilities/${shortDesc}#${section}`)
                    }   
                    return
                }

                if(page.startsWith('Industry-')){
                    props.setShowWhatWeDo(false)
                    const industry_name = page.split('-')[1]
                    const tab = page.split('-')[2] 
                    const section = tab ? tabToSectionMap[tab] : 'top'
                    const industry = Mapping.find((industry)=>industry.Desc===industry_name)
                    const shortDesc=industry?.ShortDesc
                    if (shortDesc){
                       navigate(`/capabilities/${shortDesc}#${section}`)
                    }
                    return
                }

                if(page==="insights"){
                   props.setShowWhatWeDo(false)
                   navigate('/insights')
                }
                    
                // if(page==="company"){
                //    props.setShowWhatWeDo(false)
                //    navigate('/company')
                // } 

                if(page.startsWith("company")){
                    props.setShowWhatWeDo(false)
                    const section = page.split('-')[1]
                    const path = section ? `/company#${section}` : '/company'
                    navigate(path)
                }

                if(page==="partners"){
                   props.setShowWhatWeDo(false)
                   navigate('/partners')
                } 

                if(page==="careers"){
                   props.setShowWhatWeDo(false)
                   navigate('/careers')
                } 

                if(page==="contact"){
                   props.setShowWhatWeDo(false)
                   navigate('/contact')
                } 

                if(page.startsWith('Section-')){
                    props.setShowWhatWeDo(false)
                    const tab = page.split('-')[1]
                    const section = tabToSectionMap[tab]
                    if (section)
                        navigate(`${currentPage}#${section}`)
                    return
                }

            }
            return 
        });
       
       function getRandomInt(min, max) {
        min = Math.ceil(min);
        max = Math.floor(max);
        return Math.floor(Math.random() * (max - min) + min);
    }
        async function getConfigId(name){
            try{
                const result = await axios.get(`/get_config_id/${name}`);
                return result.data.id;
            }catch(error){
                console.error("Error getting config id:", error);
                return null;
            }
        }

        async function trackUser(roomName, agentName) {
        const configId = await getConfigId(agentName);
        try {
            // Get IP from public API
            const ipRes = await axios.get('https://api.ipify.org?format=json');
            const ip = ipRes.data.ip;
            await axios.post('/track_user', {
                ip: ip,
                room_name: roomName,
                config_id: configId,
                agent_name: "avatar-agent"
            });
        } catch (error) {
            console.error("Error tracking user:", error);
            // Server detect from request
            try {
                await axios.post('/track_user', {
                    room_name: roomName,
                    config_id: configId,
                    agent_name: "avatar-agent"
                });
            } catch (e) {
                console.error("Error sending fallback tracking:", e);
            }
        }
    }

       const connect = () => {
        //create room
        const roomName = `testroom-${getRandomInt(1000, 9999)}`;
        setRoomName(roomName);
        const participantName = 'You';
        const agent = process.env.REACT_APP_AGENT_NAME;
        console.log('Agent Name:', agent);
        setAgentName(agent)
        console.log('Room Name:', roomName, 'Participant Name:', participantName);
        createRoom(roomName, participantName);
        //create agent and add to room
        createAgentSession(roomName, agent);
        setShowAvatar(true);
        setShowChatBot(false);
        setIsFallback(false);
        setHasCamTrack(false);
        trackUser(roomName, agent);
    }

    const handleFallback = () => {
        setIsFallback(true);
        room.disconnect(); // Disconnect exactly when 10 seconds is up
    };

    const handleChatWithAI = async () => {        
        // Create new room & dispatch agent WITH text mode enabled
        const newRoomName = `testroom-${getRandomInt(1000, 9999)}`;
        setRoomName(newRoomName);
        setIsFallback(false);
        setShowChatBot(true); // Switch to ChatBox directly
        await createRoom(newRoomName, 'You');
        await createAgentSession(newRoomName, agentName, true);
        trackUser(newRoomName, agentName);
    }

    async function createRoom(roomName, participantName) {
        const result = await axios.get(`/getLivekitToken/${roomName}/${participantName}`)
        console.log('Livekit token', result.data);
        room.connect(process.env.REACT_APP_LIVEKIT_URL, result.data);
    }

    async function createAgentSession(roomName, agent, textMode=false) {
        setLoading('agent-starting');
        const fullAgentName = "cloud-" + agent;
        console.log('Creating test session for agent:', fullAgentName, 'TextMode:', textMode);
        try {
            const result = await axios.post("/dispatch_agent", {
                agentName: fullAgentName, 
                roomName: roomName,
                metadata: { textMode: textMode }
            }, { headers: { "Content-Type": "application/json" } });
            console.log('Test agent session start result', result.data);
        }
        catch (error) {
            console.log("Error occurred at creating agent: ", error)
        }
        finally {
            setLoading('')
            console.log("Agent session created.");
        }
    }

    async function stopagentsession() {
        setLoading('agent-stopping');
        setShowAvatar(false);
        setShowTranscript(false)
        setShowChatBot(false)
        setIsFallback(false)
        try {
            room.disconnect();
            const result = await axios.post("/stop_agent_session", {
                "room_name": roomName
            })
            console.log(result);
        }
        catch (error) {
            console.log("Error occurred during stop agent:", error)
        }
        finally {
            setLoading('')
            console.log("Agent session stopped.");
        }
    }

    return(<>
 
     <RoomContext.Provider data-lk-theme="default" value={room}>
        {showAvatar && 
        <div style={{
                position: "fixed",
                inset: 0,
                pointerEvents: "none",
                zIndex: 999999,}}>
            <Rnd
                size={size}
                position={position}
                cancel=".control-btn, .controls"
                    onDragStop={(e, d) => {
                    setPosition({ x: d.x, y: d.y });
                    }}
                    onResizeStop={(e, direction, ref, delta, pos) => {
                    setSize({
                        width: ref.style.width,
                        height: ref.style.height,
                    });
                    setPosition(pos)
                    }}
                    bounds='parent'>
                <Container className={`agent-container ${(showTranscript) ? "transcript-container" : ""} p-0 border-none`}>
                    <div style={{ flex: (showTranscript) ? '0 0 100%' : '1', transition: 'flex 0.3s ease'}}>
                        <div className={(showTranscript) ? "video-room-with-transcript" : "video-room"}>
                            {!showChatBot && (
                                <AvatarRenderer 
                                    isFallback={isFallback}
                                    setHasCamTrack={setHasCamTrack} 
                                    onFallback={handleFallback}
                                    startChat={handleChatWithAI}
                                />
                            )}
                            {!showTranscript && hasCamTrack && !showChatBot &&
                            <Button
                                className="control-btn rounded-circle position-absolute"
                                style={{ top: '8px', left: '8px', zIndex: 10 }}
                                variant="light"
                                onClick={() => setShowTranscript(true)}
                                title="Show Transcript">
                                <i className="bi bi-chat-left-quote"></i>
                            </Button>}
                            {!isFallback && 
                             <div className="controls">
                                <MuteButton />
                                <AudioWavesRenderer/>
                                <Button
                                    className="control-btn rounded-circle text-light position-relative"
                                    variant="danger"
                                    onClick={stopagentsession}
                                    disabled={loading}>
                                    <i className="bi bi-telephone-x-fill fs-6 position-absolute top-50 start-50 translate-middle" />
                                </Button>
                            </div>}
                        </div>  
                    </div>

                    {/* Transcript panel (when avatar is active) */}
                    {showTranscript && hasCamTrack && (
                        <div className="transcript-box-container d-flex flex-column h-100" style={{ flex: '0 0 100%'}}>
                            <div className="d-flex justify-content-between align-items-center p-2 border-bottom">
                                <h6 className="m-0" style={{ fontSize: '14px' }}>Transcript</h6>
                                <Button
                                    className="control-btn rounded-circle"
                                    style={{ width: '28px', height: '28px', padding: 0 }}
                                    variant="light"
                                    onClick={() => setShowTranscript(false)}
                                    title="Close Transcript">
                                    <i className="bi bi-x"></i>
                                </Button>
                            </div>
                            <div className="flex-grow-1 overflow-auto">
                                <TranscriptBox />
                            </div>
                        </div>
                    )}

                    {/* ChatBox panel (when avatar fails to load) */}
                    {showChatBot && !hasCamTrack && (
                        <div className="transcript-box-container rounded-3 d-flex flex-column h-100" style={{ flex: '0 0 100%'}}>
                            <div className="d-flex justify-content-between align-items-center p-2 border-bottom">
                                <h6 className="m-0" style={{ fontSize: '14px' }}>Chat</h6>
                                <Button
                                    className="control-btn rounded-circle"
                                    style={{ width: '28px', height: '28px', padding: 0 }}
                                    variant="light"
                                    onClick={stopagentsession}
                                    title="Close">
                                    <i className="bi bi-x"></i>
                                </Button>
                            </div>
                            <div className="flex-grow-1 overflow-hidden d-flex flex-column">
                                <ChatBox/> 
                            </div>
                        </div>
                    )}
                </Container>
            </Rnd>
        </div>
       }

     {!showAvatar && (
        <div className="collapsed-avatar d-flex flex-column align-items-center" style={{position:'fixed', right: MARGIN, bottom: 40, zIndex:9999}}>
            <button
                onClick={connect}
                aria-label="Open assistant"
                className="btn btn-dark rounded-circle d-flex align-items-center justify-content-center shadow"
                style={{width:64, height:64, padding:0}}
            >
            {imageLoaded ? (
                        <img
                            src={process.env.PUBLIC_URL + '/avatar-meera.png'}
                            alt="assistant"
                            onError={(e) => { setImageLoaded(false); e.target.style.display = 'none'; }}
                            onLoad={() => setImageLoaded(true)}
                            style={{width:64, height:64, objectFit:'inherit', borderRadius:'50%'}}
                        />
                    ) : (
                        <i className="bi bi-chat-dots-fill" style={{fontSize:22}} />
                    )}
            </button>
            <button onClick={connect} className="btn border border-white text-white px-3 py-2 rounded mt-2" style={{backgroundColor: '#000000', fontSize:'12px'}}>May I Help?</button>    
        </div>
     )}
     </RoomContext.Provider>

    </>)
}
export default Avatar




